/**
 * Browser wrapper for the Goodbye Letter data resolver.
 *
 * The resolution logic lives in the edge-safe shared module so the browser
 * path and Edge Function path use one implementation. Browser callers supply
 * the authenticated Supabase client; the Edge Function supplies a service-role
 * client only after its server-side ownership check passes.
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import {
  GoodbyeLetterPrereqError,
  type GoodbyeLetterData,
  type GoodbyeLetterOptions,
  type GoodbyeLetterPrereqCode
} from '@/types/goodbye-letter';
import { getGoodbyeLetterData as resolveGoodbyeLetterData } from '../../supabase/functions/_shared/goodbye-letter-data';

const log = createComponentLogger('GoodbyeLetterService', 'database');

export async function getGoodbyeLetterData(
  servicingLoanId: string,
  options?: GoodbyeLetterOptions
): Promise<GoodbyeLetterData> {
  return resolveGoodbyeLetterData(supabase, servicingLoanId, options);
}

export async function getAuthorizedGoodbyeLetterData(
  servicingLoanId: string,
  options?: GoodbyeLetterOptions
): Promise<GoodbyeLetterData> {
  const { data, error } = await supabase.functions.invoke('generate-goodbye-letter-data', {
    body: { servicingLoanId, options }
  });

  if (error) {
    log.error('Goodbye Letter Edge Function invocation failed', { servicingLoanId }, error);
    throw error;
  }

  if (!data?.success) {
    const code = data?.code;
    const message = data?.error || 'Failed to generate Goodbye Letter data';
    if (code) {
      throw new GoodbyeLetterPrereqError(code as GoodbyeLetterPrereqCode, message, data?.missingFields);
    }
    throw new Error(message);
  }

  return data.data as GoodbyeLetterData;
}

function parseDownloadFilename(contentDisposition: string | null): string {
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition ?? '');
  return match?.[1] ?? 'Goodbye_Letter.pdf';
}

export async function downloadAuthorizedGoodbyeLetterPdf(
  servicingLoanId: string,
  options?: GoodbyeLetterOptions
): Promise<void> {
  const { data: session } = await supabase.auth.getSession();
  if (!session?.session?.access_token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/download-goodbye-letter`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.session.access_token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY
    },
    body: JSON.stringify({ servicingLoanId, options })
  });

  if (!response.ok || !response.headers.get('content-type')?.includes('application/pdf')) {
    const payload = await response.json().catch(() => null);
    const code = payload?.code;
    const message = payload?.error || `Failed to generate Goodbye Letter PDF (${response.status})`;
    if (code) {
      throw new GoodbyeLetterPrereqError(code as GoodbyeLetterPrereqCode, message, payload?.missingFields);
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = parseDownloadFilename(response.headers.get('content-disposition'));
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function recordGoodbyeLetterAudit(data: GoodbyeLetterData): Promise<void> {
  const { error } = await supabase.rpc('record_goodbye_letter_audit', {
    p_servicing_loan_id: data.servicingLoanId,
    p_notice_date: data.noticeDate,
    p_template_id: data.templateId,
    p_settings_id: data.settingsId,
    p_short_notice_override: data.shortNoticeOverrideUsed
  });

  if (error) {
    log.error('Failed to record Goodbye Letter audit entry', { servicingLoanId: data.servicingLoanId }, error);
    throw error;
  }
}
